import {
  Component,
  computed,
  effect,
  Input,
  OnInit,
  Optional,
  Self,
  Signal,
  signal,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { BlockLifemap, LevelNumber } from '@src/app/@shared/models/step.model';
import { NgControl } from '@angular/forms';
import { BlockAbstractComponent } from '@app/components/blocks/block.abstract.component';
import { CalendarEvent, CalendarEventTimesChangedEvent } from 'angular-calendar';
import { addHours, differenceInMinutes, getUnixTime, isAfter } from 'date-fns';
import { distinctUntilChanged, firstValueFrom, Subject } from 'rxjs';
import { CourseContentService } from '@app/courseContent/courseContent.service';
import {
  EventMetaData,
  LifemapLog,
  LifemapService,
  LifemapType,
  PlCalendarEvent,
  PlEventColor,
  plEventsColor,
  StartEndDateTime,
} from '@src/app/@shared/services/lifemap/lifemap.service';
import { LifemapEntryData } from '@app/components/blocks/block-lifemap/editLifemapEntry/editLifemapEntry.component';
import { animate, style, transition, trigger } from '@angular/animations';
import { opacityAnimation } from '@app/utils/animations';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  ModalConfirmEventsOverrideComponent,
  ModalConfirmEventsOverrideInputs,
} from '@app/components/blocks/block-lifemap/modalConfirmEventsOverride/modalConfirmEventsOverride.component';
import { PlIcon } from '@app/icon-library/icons';
import {
  ModalSleepHoursComponent,
  ModalSleepHoursInputs,
  SleepHours,
} from '@app/components/blocks/block-lifemap/modalSleepHours/modalSleepHours.component';
import {
  ModalEditLifemapEntryComponent,
  ModalEditLifemapEntryInputs,
} from '@app/components/blocks/block-lifemap/modalEditLifemapEntry/modalEditLifemapEntry.component';
import { TimeFormat } from '@app/components/pl-timepicker/pl-timepicker.component';
import {
  ModalLifemapCompletedComponent,
  ModalLifemapCompletedInputs,
} from '@app/components/blocks/block-lifemap/modalLifemapCompleted/modalLifemapCompleted.component';
import { ZendeskService } from '@shared/services/zendesk.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { roundToNearestHalfHour } from '@app/utils/date';
import { MIDNIGHT } from '@app/components/blocks/block-lifemap/pipes/calendarViewHourDisplay/calendarViewHourDisplay.pipe';
import { Breakpoint, WindowEventService } from '@shared/services/window.event.service';

interface DayDisplayMobile {
  dayNumber: number;
  selected: boolean;
}

@Component({
  selector: 'pl-block-lifemap',
  templateUrl: './block-lifemap.component.html',
  styleUrls: ['./block-lifemap.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('translateX', [
      transition('void => *', [
        style({ transform: 'translateX(100%)' }),
        animate('250ms', style({ transform: 'translateX(0px)' })),
      ]),
      transition('* => void', [animate('250ms', style({ transform: 'translateX(100%)' }))]),
    ]),
    opacityAnimation,
  ],
})
export class BlockLifemapComponent extends BlockAbstractComponent<BlockLifemap, null> implements OnInit {
  @Input({ required: true }) public timeFormat!: TimeFormat;

  public isLoading: WritableSignal<boolean> = signal(true);

  public readonly viewDate: Signal<Date> = signal(this.lifemapService.getCalendarStartDate());
  public viewDateMobile: Signal<Date> = computed(() =>
    this.getMobileStartDayFromDaysSelected(this.daysDisplayMobile()),
  );
  public refresh: Subject<void> = new Subject<void>();
  public allActivities: WritableSignal<string[]> = signal([]);
  public eventClicked: WritableSignal<PlCalendarEvent | null> = signal(null);
  public eventsToDisplay: WritableSignal<PlCalendarEvent[]> = signal([]);
  public oldStateEventsToDisplay: WritableSignal<PlCalendarEvent[]> = signal([]);
  public isStepCompleted: Signal<boolean> = computed(() => this.courseContentService.isCurrentStepCompleted());

  public daysDisplayMobile: WritableSignal<DayDisplayMobile[]> = signal(this.getDaysToDisplayForMobile());

  // why "any" and not "LifemapType" ??
  // in the template, we use "plEventsColor[weekEvent.event.meta?.type].cssClassForBorder"
  // As weekEvent is not typed in this ng-template, typescript thinks "weekEvent.event.meta?.type" is of type "any"
  // and throw the error: "Element implicitly has an 'any' type because expression of type 'any' can't be used to index type 'Record<LifemapType, PlEventColor>'."
  public plEventsColor: Record<any, PlEventColor> = plEventsColor;

  protected readonly LifemapType: typeof LifemapType = LifemapType;
  protected readonly PlIcon: typeof PlIcon = PlIcon;
  protected readonly MIDNIGHT: typeof MIDNIGHT = MIDNIGHT;

  constructor(
    @Optional() @Self() public override controlDir: NgControl,
    private windowEventService: WindowEventService,
    private zendeskService: ZendeskService,
    protected override courseContentService: CourseContentService,
    public dialog: MatDialog,
    public lifemapService: LifemapService,
  ) {
    super(controlDir, courseContentService);

    this.hostBindingClasses.set(new Set(['block']));

    effect(() => {
      this.lifemapService.logEvents(
        this.eventsToDisplay(),
        'BlockLifemapComponent REFRESH VIEW this.eventsToDisplay()',
      );
      this.eventsToDisplay();
      this.refresh.next();
    });

    this.zendeskService.hideWidget();

    this.windowEventService
      .getWindowResizeEvent()
      .pipe(distinctUntilChanged())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((size: number): void => {
        console.log(
          'TONIO BlockLifemapComponent RESIZE size=',
          size,
          ' this.dialog.openDialogs=',
          this.dialog.openDialogs,
          ' this.eventClicked()=',
          this.eventClicked(),
        );
        // If the user resize the screen from Desktop to Mobile (using a tablet and changing the orientation), he might be in the middle of editing an entry
        // with this, we will show the modal to edit and he won't loose any data he entered
        const eventClicked: PlCalendarEvent | null = this.eventClicked();
        if (size < Breakpoint.lg && eventClicked !== null && this.dialog.openDialogs.length === 0) {
          this.displayModalEditLifeMapEntry(eventClicked);
        }
      });
  }

  protected override initForm(): void {}

  protected override patchFormWithEmptyValue(): void {
    this.lifemapService.resetLifemapData();
    this.setEventsToDisplay([]);
  }

  protected override patchFormFromBlock(block: BlockLifemap): void {
    this.lifemapService
      .retrieveParticipantLifeMap(block)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: async (events: PlCalendarEvent[]): Promise<void> => {
          this.lifemapService.logEvents(
            events,
            'BlockLifemapComponent patchFormFromBlock retrieveParticipantLifeMap events',
          );

          if (events.length === 0 && !this.courseContentService.isCurrentStepCompleted()) {
            this.setEventsToDisplay(this.lifemapService.fillGapsWithSpecialEventsAndSortAndMergeSiblings([]));
            const dialogRef: MatDialogRef<ModalSleepHoursComponent, SleepHours | null> = this.dialog.open<
              ModalSleepHoursComponent,
              ModalSleepHoursInputs,
              SleepHours | null
            >(ModalSleepHoursComponent, {
              data: {
                timeFormat: this.timeFormat,
              },
              height: '520px',
              maxWidth: '500px',
            });

            this.isLoading.set(false);
            const sleepHours: SleepHours | null | undefined = await firstValueFrom(dialogRef.afterClosed());

            const sleepEventsWithNewLifemapBlocks: PlCalendarEvent[] =
              this.lifemapService.fillGapsWithSpecialEventsAndSortAndMergeSiblings(
                sleepHours ? this.addSleepEvents(sleepHours) : [],
              );
            this.syncEventsWithServer(sleepEventsWithNewLifemapBlocks);
            this.setEventsToDisplay(this.getEventsDisablingAllAfterNewEntry(sleepEventsWithNewLifemapBlocks));
          } else {
            const eventsWithNewLifemapBlocks: PlCalendarEvent[] =
              this.lifemapService.fillGapsWithSpecialEventsAndSortAndMergeSiblings(events);
            this.setEventsToDisplay(this.getEventsDisablingAllAfterNewEntry(eventsWithNewLifemapBlocks));
            this.displayLifeMapCompleteModalIfLifeMapCompleted(eventsWithNewLifemapBlocks);
            this.computeDayToSelectOnMobileBasedOnFirstAddNewEntry(eventsWithNewLifemapBlocks);
            this.allActivities.set(this.lifemapService.getActivitiesFromEvents(eventsWithNewLifemapBlocks));
            this.isLoading.set(false);
          }
        },
      });
  }

  private setEventsToDisplay(events: PlCalendarEvent[]): void {
    this.eventsToDisplay.set(events);
  }

  public validateEventTimesChanged(eventChanged: CalendarEventTimesChangedEvent<EventMetaData>): boolean {
    console.log('TONIO BlockLifemapComponent validateEventTimesChanged eventChanged=', eventChanged);
    if (this.isStepCompleted()) {
      console.log('TONIO BlockLifemapComponent validateEventTimesChanged RETURN FALSE =');
      return false;
    }

    const newStart: Date = eventChanged.newStart;
    const newEnd: Date | undefined = eventChanged.newEnd;
    if (newEnd && eventChanged.event.end) {
      const thirtyMinInSeconds: number = 30 * 60;
      const twelveHoursInSeconds: number = 12 * 60 * 60;
      const oldEventDurationInSeconds: number =
        getUnixTime(eventChanged.event.end) - getUnixTime(eventChanged.event.start);
      const newEventDurationInSeconds: number = getUnixTime(newEnd) - getUnixTime(newStart);
      if (
        newEventDurationInSeconds < thirtyMinInSeconds - 2 ||
        (newEventDurationInSeconds > twelveHoursInSeconds && newEventDurationInSeconds > oldEventDurationInSeconds)
      ) {
        // Why "newEventDurationInSeconds < thirtyMinInSeconds - 2" ???
        //  if the event ends at the end of the day, its end time will be 23:59:59.999
        //  if the user tries to reduce the event by moving its start time to 23:30, the duration will be less than 30min (1799 seconds)
        //   => we do "-2" to cover this case and it's not impacting all the rest
        console.log(
          'TONIO BlockLifemapComponent validateEventTimesChanged 1111 RETURN FALSE newEventDurationInSeconds=',
          newEventDurationInSeconds,
          '   thirtyMinInSeconds=',
          thirtyMinInSeconds,
        );
        return false;
      }

      return true;
    } else {
      console.error(`No end (or newEnd) time for event: `, eventChanged);
      throw new Error(`No end (or newEnd) on event, this is not normal`);
    }
  }

  public async eventTimesChanged(eventChanged: CalendarEventTimesChangedEvent<EventMetaData>): Promise<void> {
    if (this.isStepCompleted()) {
      return;
    }

    if (eventChanged.newEnd && eventChanged.event.end && eventChanged.event.id) {
      // It's possible that start and end time are not rounded by angular-calendar to the nearest 30min
      // it can happen when the user move the mouse outside of the current application while dragging an event
      eventChanged.newStart = roundToNearestHalfHour(eventChanged.newStart);
      eventChanged.newEnd = this.lifemapService.getEndTime(eventChanged.newEnd);

      const oldEvents: PlCalendarEvent[] = this.eventsToDisplay();
      const eventsUpdatedWithoutComputation: PlCalendarEvent[] = this.lifemapService.applyEventChangeWithoutComputation(
        eventChanged,
        oldEvents,
      );

      const oldEventClicked: PlCalendarEvent | null = this.eventClicked();
      if (oldEventClicked) {
        // EditLifemapEntry component is open, just update timings
        this.eventClicked.set({
          ...oldEventClicked,
          start: eventChanged.newStart,
          end: eventChanged.newEnd,
        });
        this.setEventsToDisplay(eventsUpdatedWithoutComputation);
      } else {
        const eventsOverridden: PlCalendarEvent[] = this.lifemapService.getEventsOverlappingWithEventUpdated(
          {
            id: eventChanged.event.id,
            newStart: eventChanged.newStart,
            newEnd: eventChanged.newEnd,
          },
          oldEvents,
        );

        this.lifemapService.logEvents(eventsOverridden, 'BlockLifemapComponent eventTimesChanged eventsOverridden');

        if (eventsOverridden.length > 0) {
          this.setEventsToDisplay(eventsUpdatedWithoutComputation);
          const dialogRef: MatDialogRef<ModalConfirmEventsOverrideComponent, boolean | undefined> = this.dialog.open<
            ModalConfirmEventsOverrideComponent,
            ModalConfirmEventsOverrideInputs,
            boolean | undefined
          >(ModalConfirmEventsOverrideComponent, {
            data: {
              eventsOverridden,
              timeFormat: this.timeFormat,
            },
            maxWidth: '800px',
          });

          const userClickedConfirmOnTheModal: boolean | undefined = await firstValueFrom(dialogRef.afterClosed());

          if (!userClickedConfirmOnTheModal) {
            this.setEventsToDisplay(oldEvents);
            return;
          }
        }

        const eventsUpdatedWithComputation: PlCalendarEvent[] = this.lifemapService.applyEventChangeWithComputation(
          {
            newEnd: eventChanged.newEnd,
            newStart: eventChanged.newStart,
            id: eventChanged.event.id,
          },
          oldEvents,
        );
        this.setEventsToDisplay(this.getEventsDisablingAllAfterNewEntry(eventsUpdatedWithComputation));
        this.syncEventsWithServer(eventsUpdatedWithComputation);
      }
    } else {
      console.error(
        `eventTimesChanged() - eventChanged.newEnd or eventChanged.event.end is not defined, this is not normal. eventChanged=`,
        eventChanged,
      );
      throw new Error(
        `eventTimesChanged() - eventChanged.newEnd or eventChanged.event.end is not defined, this is not normal`,
      );
    }
  }

  public async onEventClickedMobile(eventClicked: CalendarEvent<EventMetaData>): Promise<void> {
    if (this.isStepCompleted()) {
      return;
    }

    if (eventClicked.end && eventClicked.meta && typeof eventClicked.id === 'string') {
      const plEventClicked: PlCalendarEvent = eventClicked as PlCalendarEvent;
      if (eventClicked.meta.type !== LifemapType.blocked) {
        this.oldStateEventsToDisplay.set(this.lifemapService.deepCopyEvents(this.eventsToDisplay()));
        this.setEventsToDisplay(
          this.getEventsWithGrayscaleEffectOnNonSelectedEvents(this.eventsToDisplay(), plEventClicked),
        );
        const plEventClickedCopy: PlCalendarEvent = this.lifemapService.deepCopyEvent(plEventClicked);
        plEventClickedCopy.end = this.getNewEndTimeForEventClickedIfItIsNewEntry(plEventClickedCopy);
        this.eventClicked.set(plEventClickedCopy);

        this.displayModalEditLifeMapEntry(plEventClickedCopy);
      }
    } else {
      console.error(`end & meta should always be defined, id should be a string - event=`, eventClicked);
      throw new Error(`end & meta should always be defined, id should be a string, bug somewhere`);
    }
  }

  private async displayModalEditLifeMapEntry(eventToEdit: PlCalendarEvent): Promise<void> {
    if (this.block) {
      const modalInputs: ModalEditLifemapEntryInputs = {
        event: eventToEdit,
        allActivities: this.allActivities(),
        blockLifemap: this.block,
        timeFormat: this.timeFormat,
      };
      const dialogRef: MatDialogRef<ModalEditLifemapEntryComponent, LifemapEntryData | null | undefined> =
        this.dialog.open<
          ModalEditLifemapEntryComponent,
          ModalEditLifemapEntryInputs,
          LifemapEntryData | null | undefined
        >(ModalEditLifemapEntryComponent, {
          data: modalInputs,
          height: '100vh',
          width: '100vw',
          maxWidth: '100vw',
        });

      const entryModified: LifemapEntryData | null | undefined = await firstValueFrom(dialogRef.afterClosed());

      if (entryModified) {
        this.onEditEntryUpdated(entryModified);
        await this.saveLifeMapEntry(entryModified);
      } else {
        this.discardChangesOnEditEvent();
      }

      return;
    } else {
      throw new Error(`Block should be defined when displaying modal to edit lifemap entry`);
    }
  }

  public onEventClicked(eventClicked: CalendarEvent<EventMetaData>): void {
    console.log('TONIO BlockLifemapComponent onEventClicked this.eventClicked=', this.eventClicked());
    if (this.isStepCompleted() || this.eventClicked() !== null) {
      // this.isStepCompleted(): Additional Security to prevent user from interacting with the page if they remove the blur overlay effect in the console
      // this.eventClicked() !== null: the editLifemap component is already open, we will save an "incorrect" status for oldStateEventsToDisplay if we go through this function again
      return;
    }

    if (eventClicked.end && eventClicked.meta && typeof eventClicked.id === 'string') {
      const plEventClicked: PlCalendarEvent = eventClicked as PlCalendarEvent;
      this.lifemapService.logEvents([plEventClicked], 'BlockLifemapComponent onEventClicked');
      if (plEventClicked.meta.type !== LifemapType.blocked) {
        this.oldStateEventsToDisplay.set(this.lifemapService.deepCopyEvents(this.eventsToDisplay()));
        this.setEventsToDisplay(
          this.getEventsWithGrayscaleEffectOnNonSelectedEvents(this.eventsToDisplay(), plEventClicked),
        );
        const plEventClickedCopy: PlCalendarEvent = this.lifemapService.deepCopyEvent(plEventClicked);
        plEventClickedCopy.end = this.getNewEndTimeForEventClickedIfItIsNewEntry(plEventClickedCopy);
        this.eventClicked.set(plEventClickedCopy);

        this.lifemapService.logEvents(
          this.oldStateEventsToDisplay(),
          'BlockLifemapComponent onEventClicked oldStateEventsToDisplay',
        );

        // In case the user resize his screen and goes from desktop to mobile while he has something selected, we want to display the correct day
        this.selectDayForMobileDisplay(this.lifemapService.getDayNumberForEvent(plEventClicked));
      }
    } else {
      console.error(`end & meta should always be defined, id should be a string - event=`, eventClicked);
      throw new Error(`end & meta should always be defined, id should be a string, bug somewhere`);
    }
  }

  private getNewEndTimeForEventClickedIfItIsNewEntry(eventClicked: PlCalendarEvent): Date {
    if (
      eventClicked.meta.type === LifemapType.addNewEntry &&
      differenceInMinutes(eventClicked.end, eventClicked.start) > 30
    ) {
      return addHours(eventClicked.start, 1);
    }

    return eventClicked.end;
  }

  public addSleepEvents(sleepHours: SleepHours): PlCalendarEvent[] {
    const events: PlCalendarEvent[] = [];
    if (
      sleepHours.sleepAt > '12:00' &&
      sleepHours.sleepAt < '24:00' &&
      sleepHours.wakeUpAt > '00:00' &&
      sleepHours.wakeUpAt < '12:00'
    ) {
      const startDateTime: Date = this.lifemapService.getCalendarStartDate();
      const endDateTime: Date = this.lifemapService.getDateTimeBasedOnStartDate(sleepHours.wakeUpAt);
      events.push(this.lifemapService.createSleepEvent(startDateTime, endDateTime));
    }

    for (let day: number = 0; day < this.lifemapService.NUMBER_DAYS_LIFEMAP; day++) {
      const startDateForCurrentDay: Date = this.lifemapService.getDateTimeBasedOnStartDate('00:00', day);
      const dates: StartEndDateTime = this.lifemapService.computeNewStartEndDatesBasedOnTime(
        startDateForCurrentDay,
        sleepHours.sleepAt,
        sleepHours.wakeUpAt,
      );
      events.push(this.lifemapService.createSleepEvent(dates.startDateTime, dates.endDateTime));
    }

    return this.lifemapService.sortEventByDates(events);
  }

  public discardChangesOnEditEvent(): void {
    this.lifemapService.logEvents(this.oldStateEventsToDisplay(), 'discardChangesOnEditEvent oldStateEventsToDisplay=');
    this.eventClicked.set(null);
    this.setEventsToDisplay(this.getEventsDisablingAllAfterNewEntry(this.oldStateEventsToDisplay()));
    this.oldStateEventsToDisplay.set([]);
  }

  public closeEditEvent(): void {
    this.eventClicked.set(null);
    this.oldStateEventsToDisplay.set([]);
    this.setEventsToDisplay(this.getEventsDisablingAllAfterNewEntry(this.eventsToDisplay()));
  }

  public onEditEntryUpdated(editEntryData: LifemapEntryData): void {
    const currentEventClicked: PlCalendarEvent | null = this.eventClicked();
    if (currentEventClicked) {
      this.eventsToDisplay.update((events: PlCalendarEvent[]): PlCalendarEvent[] => {
        return events.map((event: PlCalendarEvent): PlCalendarEvent => {
          if (event.id === currentEventClicked.id) {
            const { startDateTime, endDateTime }: StartEndDateTime =
              this.lifemapService.computeNewStartEndDatesBasedOnTime(
                currentEventClicked.start,
                editEntryData.timeFrom,
                editEntryData.timeTo,
              );

            const updatedEvent: PlCalendarEvent = this.lifemapService.getUpdatedEventFromEventChanged(event, {
              id: editEntryData.eventId,
              newStart: startDateTime,
              newEnd: endDateTime,
              answers: editEntryData.answers,
              type: editEntryData.type,
              title: editEntryData.activityName,
            });

            this.eventClicked.set(updatedEvent);

            return updatedEvent;
          }

          return event;
        });
      });
    } else {
      throw new Error(`eventClicked() is not defined, this is not normal`);
    }
  }

  private getEventsWithGrayscaleEffectOnNonSelectedEvents(
    events: PlCalendarEvent[],
    eventSelected: PlCalendarEvent,
  ): PlCalendarEvent[] {
    return events.map((eventToUpdate: PlCalendarEvent): PlCalendarEvent => {
      const copyOfEvent: PlCalendarEvent = this.lifemapService.deepCopyEvent(eventToUpdate);
      if (copyOfEvent.id !== eventSelected.id) {
        copyOfEvent.cssClass = this.lifemapService.computeCssClassForPlEvent(
          copyOfEvent.meta.type,
          'opacity-grayscale pointer-events-none',
        );
        copyOfEvent.meta.isSelected = false;
      } else {
        let cssClassToAdd: string = 'event-currently-selected';
        const isNewEntry: boolean = copyOfEvent.meta.type === LifemapType.addNewEntry;
        if (isNewEntry) {
          cssClassToAdd += ' new-entry';
        }
        copyOfEvent.cssClass = this.lifemapService.computeCssClassForPlEvent(copyOfEvent.meta.type, cssClassToAdd);
        copyOfEvent.resizable = {
          beforeStart: !isNewEntry,
          afterEnd: true,
        };
        copyOfEvent.end = this.getNewEndTimeForEventClickedIfItIsNewEntry(copyOfEvent);
        copyOfEvent.meta.isSelected = true;
      }
      return copyOfEvent;
    });
  }

  private getEventsDisablingAllAfterNewEntry(events: PlCalendarEvent[]): PlCalendarEvent[] {
    const lastAddNewEntryEvent: PlCalendarEvent | null = this.lifemapService.getLastAddNewEntryEvent(events);
    return events.map((event: PlCalendarEvent): PlCalendarEvent => {
      if (event.meta.type === LifemapType.blocked) {
        event.cssClass = 'pointer-events-none';
      } else if (lastAddNewEntryEvent && isAfter(event.start, lastAddNewEntryEvent.start)) {
        event.cssClass = 'opacity-grayscale pointer-events-none';
      } else {
        event.cssClass = '';
      }
      event.meta.isSelected = false;
      return event;
    });
  }

  public async saveLifeMapEntry(editLifemapData: LifemapEntryData): Promise<void> {
    const eventClicked: PlCalendarEvent | null = this.eventClicked();
    if (eventClicked && eventClicked.id) {
      const { startDateTime, endDateTime }: StartEndDateTime = this.lifemapService.computeNewStartEndDatesBasedOnTime(
        eventClicked.start,
        editLifemapData.timeFrom,
        editLifemapData.timeTo,
      );

      const eventsOverridden: PlCalendarEvent[] = this.lifemapService.getEventsOverlappingWithEventUpdated(
        {
          id: eventClicked.id,
          newStart: startDateTime,
          newEnd: endDateTime,
        },
        this.eventsToDisplay(),
      );

      if (eventsOverridden.length > 0) {
        const dialogRef: MatDialogRef<ModalConfirmEventsOverrideComponent, boolean | undefined> = this.dialog.open<
          ModalConfirmEventsOverrideComponent,
          ModalConfirmEventsOverrideInputs,
          boolean | undefined
        >(ModalConfirmEventsOverrideComponent, {
          data: {
            eventsOverridden,
            timeFormat: this.timeFormat,
          },
        });

        const userClickedConfirmOnTheModal: boolean | undefined = await firstValueFrom(dialogRef.afterClosed());
        if (!userClickedConfirmOnTheModal) {
          this.discardChangesOnEditEvent();
          return;
        }
      }

      const eventsUpdated: PlCalendarEvent[] = this.lifemapService.applyEventChangeWithComputation(
        {
          newStart: startDateTime,
          newEnd: endDateTime,
          id: editLifemapData.eventId,
          type: editLifemapData.type,
          title: editLifemapData.activityName,
          answers: editLifemapData.answers,
        },
        this.oldStateEventsToDisplay(),
      );

      this.setEventsToDisplay(eventsUpdated);
      this.computeDayToSelectOnMobileBasedOnFirstAddNewEntry(eventsUpdated);
      this.closeEditEvent();
      this.syncEventsWithServer(eventsUpdated);
    } else {
      throw new Error(`this.eventClicked is null, not normal`);
    }
  }

  private async syncEventsWithServer(eventsToSync: PlCalendarEvent[]): Promise<void> {
    if (this.block) {
      const lifemapNumber: LevelNumber = this.block.data.config.lifeMapNumber;
      this.allActivities.set(this.lifemapService.getActivitiesFromEvents(eventsToSync));

      const allLifemapLogsFromEvents: LifemapLog[] = this.lifemapService.convertEventsToLifeMapLogs(eventsToSync);
      console.log(
        'TONIO BlockLifemapComponent syncEventsWithServer allLifemapLogsFromEvents=',
        allLifemapLogsFromEvents,
      );

      await this.lifemapService.saveChangedLifemapLogs(allLifemapLogsFromEvents, lifemapNumber);

      this.displayLifeMapCompleteModalIfLifeMapCompleted(eventsToSync);
    }
  }

  public selectDayForMobileDisplay(dayNumberToSelect: number): void {
    this.daysDisplayMobile.update((daysDisplayMobile: DayDisplayMobile[]): DayDisplayMobile[] => {
      return daysDisplayMobile.map((day: DayDisplayMobile): DayDisplayMobile => {
        return {
          ...day,
          selected: day.dayNumber === dayNumberToSelect,
        };
      });
    });
  }

  private getDaysToDisplayForMobile(): DayDisplayMobile[] {
    const daysDisplay: DayDisplayMobile[] = [];
    for (let index: number = 0; index < this.lifemapService.NUMBER_DAYS_LIFEMAP; index++) {
      daysDisplay.push({
        dayNumber: index,
        selected: index === 0,
      });
    }

    return daysDisplay;
  }

  private getMobileStartDayFromDaysSelected(dayDisplayMobile: DayDisplayMobile[]): Date {
    for (const day of dayDisplayMobile) {
      if (day.selected) {
        return this.lifemapService.getDateTimeBasedOnStartDate('00:00', day.dayNumber);
      }
    }

    return this.lifemapService.getCalendarStartDate();
  }

  private computeDayToSelectOnMobileBasedOnFirstAddNewEntry(events: PlCalendarEvent[]): void {
    const dayNumberForFirstEntry: number = this.lifemapService.getDayNumberForFirstAddNewEntry(events);
    if (dayNumberForFirstEntry === this.lifemapService.NUMBER_DAYS_LIFEMAP) {
      // Select last day if everything is completed
      this.selectDayForMobileDisplay(this.lifemapService.NUMBER_DAYS_LIFEMAP - 1);
    } else {
      this.selectDayForMobileDisplay(dayNumberForFirstEntry);
    }
  }

  public async displayLifeMapCompleteModalIfLifeMapCompleted(events: PlCalendarEvent[]): Promise<void> {
    const isLocalCalendarFullyCompleted: boolean = this.lifemapService.isLocalCalendarFullyCompleted(events);
    const isLifemapLogsFullySyncedWithServer: boolean = this.lifemapService.isLifemapLogsFullyCompleted();
    console.log(
      '\x1b[41m TONIO BlockLifemapComponent displayLifeMapCompleteModalIfLifeMapCompleted isLocalCalendarFullyCompleted=',
      isLocalCalendarFullyCompleted,
      'isLifemapLogsFullySyncedWithServer=',
      isLifemapLogsFullySyncedWithServer,
      '\x1b',
    );
    if (isLocalCalendarFullyCompleted || this.courseContentService.isCurrentStepCompleted()) {
      const modalInputs: ModalLifemapCompletedInputs = {
        events,
        lifemapBlock: this.block as BlockLifemap,
        isLifemapLogsFullySyncedWithServer,
      };
      this.dialog.open<ModalLifemapCompletedComponent, ModalLifemapCompletedInputs, void>(
        ModalLifemapCompletedComponent,
        {
          data: modalInputs,
          maxWidth: '600px',
          width: '600px',
          disableClose: true,
        },
      );
    }

    if (isLifemapLogsFullySyncedWithServer) {
      this.courseContentService.showSnackbar();
    } else {
      this.courseContentService.hideSnackbar();
    }
  }
}
